import * as React from 'react';
import Box from '@mui/material/Box';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export interface FilaEsperaToggleProps {
  value: boolean | null | undefined;
  onChange: (value: boolean | null) => void;
  disabled?: boolean;
}

type ToggleValue = 'ativar' | 'desativar' | 'torneio';

function toggleValueOf(value: boolean | null | undefined): ToggleValue {
  if (value === true) return 'ativar';
  if (value === false) return 'desativar';
  return 'torneio'; // null ou undefined → herda do torneio
}

function fromToggleValue(value: ToggleValue): boolean | null {
  if (value === 'ativar') return true;
  if (value === 'desativar') return false;
  return null;
}

const helpText = (
  <Box sx={{ p: 0.5 }}>
    <Typography variant="caption" component="div" sx={{ fontWeight: 600, mb: 0.5 }}>
      Fila de espera desta categoria
    </Typography>
    <Box component="ul" sx={{ m: 0, pl: 2 }}>
      <li><b>Ativar:</b> esta categoria sempre terá fila de espera quando as vagas estiverem esgotadas.</li>
      <li><b>Desativar:</b> esta categoria nunca terá fila de espera; inscrições além das vagas são bloqueadas.</li>
      <li><b>Torneio:</b> usa a configuração geral do torneio.</li>
    </Box>
  </Box>
);

export const FilaEsperaToggle: React.FC<FilaEsperaToggleProps> = ({ value, onChange, disabled }) => {
  const handleChange = (_: React.MouseEvent<HTMLElement>, next: ToggleValue | null) => {
    // ToggleButtonGroup exclusive permite "nenhum selecionado" — ignoramos para manter tri-estado.
    if (next === null) return;
    onChange(fromToggleValue(next));
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <ToggleButtonGroup
        value={toggleValueOf(value)}
        exclusive
        onChange={handleChange}
        size="small"
        disabled={disabled}
        aria-label="Configuração de fila de espera da categoria"
      >
        <ToggleButton value="ativar" aria-label="Ativar fila de espera">Ativar</ToggleButton>
        <ToggleButton value="desativar" aria-label="Desativar fila de espera">Desativar</ToggleButton>
        <ToggleButton value="torneio" aria-label="Usar configuração do torneio">Torneio</ToggleButton>
      </ToggleButtonGroup>
      <Tooltip title={helpText} placement="top" arrow>
        <InfoOutlinedIcon
          fontSize="small"
          sx={{ color: 'text.secondary', cursor: 'help' }}
          aria-label="Informações sobre fila de espera por categoria"
        />
      </Tooltip>
    </Box>
  );
};

export default FilaEsperaToggle;
